import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {child, get, update} from "firebase/database";
import {AUTH, dbRef} from "../../auth/FirebaseContext";
import {getIniLoggedUser, getSessionSelectedLocation} from "../../helper/session";
import {updateSeatSelectionEnabled, updateA4PrintEnabled} from "./settings";
import {getItemsForSaleAPI} from "../../api/firebase";

/**
 * Tekst reklame na karti živi pod PRIMARNIM nalogom (isti čvor iz kog ga
 * setDefaultUserSettings čita) — forma podešavanja ga upisuje ovde da pregled
 * i štampa karte uvek nose isti tekst.
 */
export const updateAdvertisementTextTicket =
    createAsyncThunk("updateAdvertisementTextTicket", async (text) => {
        const primaryUser = getIniLoggedUser();
        await update(child(dbRef, `users/${primaryUser.uid}/private/settings/bussoft/web`),
            {advertisementTextTicket: text ?? ""});
        return text ?? "";
    });


export const setDefaultUserSettings =
    createAsyncThunk("setDefaultUserSettings", async () => {
        const primaryUser = getIniLoggedUser();
        if (AUTH.currentUser) {
            try {
                await AUTH.currentUser.getIdToken(true);
            } catch (e) {
                console.error("getIdToken(true) pre učitavanja podešavanja:", e);
            }
        }
        let settings = (await get(child(dbRef, `users/${primaryUser.uid}/private/settings/bussoft/web`))).val();
        const location = getSessionSelectedLocation()?.PAC;
        const locationStations = location && settings?.defaultOutboundStationsByLocation?.[location];
        const rawStations = locationStations || settings?.defaultOutboundStations || [];
        let defaultOutboundStations = Array.isArray(rawStations) ? rawStations : [rawStations];
        if (defaultOutboundStations?.[0]) {
            localStorage.setItem("defaultStation", JSON.stringify(defaultOutboundStations[0]));
        }
        const locationShortcuts = location && settings?.stationShortcutsByLocation?.[location];
        const stationShortcuts = Array.isArray(locationShortcuts) ? locationShortcuts : [];
        let services = [];

        let forbiddenPages = settings?.forbiddenPages || [];

        if (!location) {
            return {
                userType: settings?.userType,
                services,
                defaultOutboundStations,
                stationShortcuts,
                forbiddenPages,
                issuingTicket: !!settings?.issuingTicket,
                roundedPrice: settings?.roundedPrice ?? true,
                advertisementTextTicket: settings?.advertisementTextTicket || "",
                canSellExpiredDepartures: !!settings?.canSellExpiredDepartures,
                expiredDepartureMinutesLimit: settings?.expiredDepartureMinutesLimit || 0,
            }
        }

        // Učitaj stavke za izdavanje karata iz backend API-ja (sa cenama iz aktivnog cenovnika)
        try {
            const response = await getItemsForSaleAPI(location);
            services = response.data || [];
        } catch (error) {
            console.error("Greška pri učitavanju stavki za prodaju:", error);
            services = [];
        }

        return {
            ...settings,
            defaultOutboundStations,
            stationShortcuts,
            forbiddenPages,
            services,
            issuingTicket: !!settings.issuingTicket,
            roundedPrice: settings.roundedPrice ?? true,
            advertisementTextTicket: settings?.advertisementTextTicket || "",
            canSellExpiredDepartures: !!settings.canSellExpiredDepartures,
            expiredDepartureMinutesLimit: settings.expiredDepartureMinutesLimit || 0,
        }
    });

const initialState = {
    favoriteDestinations: [],
    services: [],
    forbiddenPages: [],
    defaultDepStations: null,
    ticketPriceConfirmation: null,
    stationShortcuts: [],
    seatSelectionEnabled: false,
    a4PrintEnabled: false,
    canSellExpiredDepartures: false,
    expiredDepartureMinutesLimit: 0,
    loading: true,
    fetched: undefined,
};

const slice = createSlice({
    name: 'userSettings',
    initialState,
    reducers: {
        resetIsFetchedUserSettings: state => {
            state.loading = true;
            state.fetched = undefined;
            state.loading = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setDefaultUserSettings.pending, (state) => {
                state.loading = true;
            })
            .addCase(setDefaultUserSettings.fulfilled, (state, {payload}) => {
                state.services = payload.services;
                state.defaultDepStations = payload.defaultOutboundStations;
                state.favoriteDestinations = payload?.favoriteDestinations || [];
                state.forbiddenPages = payload?.forbiddenPages;
                state.ticketPriceConfirmation = payload.ticketPriceConfirmation;
                state.stationShortcuts = payload?.stationShortcuts || [];
                state.seatSelectionEnabled = !!payload.seatSelectionEnabled;
                state.a4PrintEnabled = !!payload.a4PrintEnabled;
                state.issuingTicket = payload.issuingTicket;
                state.roundedPrice = payload.roundedPrice;
                state.advertisementTextTicket = payload.advertisementTextTicket;
                state.userType = payload.userType;
                state.canSellExpiredDepartures = payload.canSellExpiredDepartures;
                state.expiredDepartureMinutesLimit = payload.expiredDepartureMinutesLimit;
                state.fetched = new Date();
                state.loading = false;
            })
            .addCase(setDefaultUserSettings.rejected, (state) => {
                state.loading = false;
                state.fetched = undefined;
            })
            .addCase(updateAdvertisementTextTicket.fulfilled, (state, {payload}) => {
                state.advertisementTextTicket = payload;
            })
            .addCase(updateSeatSelectionEnabled.fulfilled, (state, {meta}) => {
                state.seatSelectionEnabled = !!meta.arg;
            })
            .addCase(updateA4PrintEnabled.fulfilled, (state, {meta}) => {
                state.a4PrintEnabled = !!meta.arg;
            })
    },
});
export const {resetIsFetchedUserSettings} = slice.actions
export default slice.reducer;
